import { useState } from "react";
import { Check, Flag, RotateCw, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatError } from "@/lib/api";

const STATUS_STYLE = { known: "bg-emerald-100 text-emerald-800", revise: "bg-amber-100 text-amber-800", new: "bg-elevated text-ink-2" };

export default function FlashcardViewer({ deck, onChange }) {
  const [onlyRevise, setOnlyRevise] = useState(false);
  const cards = onlyRevise ? deck.cards.filter((c) => c.status === "revise") : deck.cards;
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const card = cards[Math.min(i, cards.length - 1)];

  const go = (d) => { setFlipped(false); setI((v) => (v + d + cards.length) % cards.length); };
  const mark = async (status) => {
    try {
      await api.patch(`/flashcards/${deck.id}/cards/${card.id}`, { status });
      onChange({ ...deck, cards: deck.cards.map((c) => (c.id === card.id ? { ...c, status } : c)) });
      if (!onlyRevise) go(1);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="flashcard-viewer" className="mx-auto max-w-2xl">
      <div className="mb-4 flex items-center justify-between text-sm">
        <span data-testid="flashcard-position" className="font-mono text-ink-3">{cards.length ? `${Math.min(i, cards.length - 1) + 1} / ${cards.length}` : "0 / 0"}</span>
        <button data-testid="flashcard-filter-toggle" onClick={() => { setOnlyRevise(!onlyRevise); setI(0); setFlipped(false); }}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${onlyRevise ? "border-amber-400 bg-amber-50 text-amber-800" : "border-line text-ink-2 hover:border-ink-3"}`}>
          {onlyRevise ? "Showing: needs revision" : "Show only needs revision"}
        </button>
      </div>
      {!card ? (
        <p data-testid="flashcard-empty" className="rounded-xl border border-dashed p-10 text-center text-sm text-ink-3">No cards marked for revision. Great work!</p>
      ) : (
        <>
          <div className="flip-card h-72 cursor-pointer sm:h-80" onClick={() => setFlipped(!flipped)} data-testid="flashcard">
            <div className={`flip-inner h-full ${flipped ? "flipped" : ""}`}>
              <div className="flip-face flex flex-col rounded-2xl border border-line bg-surface p-8 shadow-[0_10px_30px_-12px_rgba(10,31,68,.15)]">
                <span className={`self-start rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_STYLE[card.status]}`}>{card.status}</span>
                <p data-testid="flashcard-front" className="m-auto text-center text-xl font-bold text-ink font-display">{card.front}</p>
                <p className="text-center text-xs text-ink-3">Tap to reveal answer</p>
              </div>
              <div className="flip-face flip-back flex flex-col rounded-2xl bg-band p-8 text-white">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-cyan)]">Answer</span>
                <p data-testid="flashcard-back" className="m-auto text-center text-base leading-relaxed sm:text-lg">{card.back}</p>
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <Button data-testid="flashcard-prev-btn" variant="outline" size="icon" onClick={() => go(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="flashcard-reveal-btn" variant="ghost" onClick={() => setFlipped(!flipped)}><RotateCw className="mr-2 h-4 w-4" />Flip</Button>
              <Button data-testid="flashcard-revise-btn" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" onClick={() => mark("revise")}><Flag className="mr-2 h-4 w-4" />Needs revision</Button>
              <Button data-testid="flashcard-known-btn" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => mark("known")}><Check className="mr-2 h-4 w-4" />I know this</Button>
            </div>
            <Button data-testid="flashcard-next-btn" variant="outline" size="icon" onClick={() => go(1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </>
      )}
    </div>
  );
}
